import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { UserProvider } from "./user_context";
import LandingPage from "./landing_page";
import SignIn from "./sign_in";
import BrandLoader from "./components/BrandLoader";
import ErrorBoundary from "./components/ErrorBoundary";
import ConsentBanner from "./components/ConsentBanner";
import useRouteTransition from "./hooks/useRouteTransition";
import AdminRoute from "./AdminRoute";
import { resumeAnalyticsIfConsented, trackPageview } from "./lib/analytics";

// Lazy so its CSS import (global-styles-portal.js) lands in this chunk
// instead of the initial bundle — anonymous landing-page visitors never
// download portal-only styles. Still resolves under the same top-level
// Suspense as every other route, so there's no new loading state to handle.
const ProtectedRoute = lazy(() => import("./ProtectedRoute"));

const SignUp = lazy(() => import("./sign_up"));
const ForgotPassword = lazy(() => import("./forgot_password"));
const VerifyEmail = lazy(() => import("./verify_email"));
const ResetPassword = lazy(() => import("./reset-password"));

//job seeker
const JobSeekerDashboard = lazy(() => import("./portal/jobseeker/job_seeker_dashboard"));
const BrowseJobs = lazy(() => import("./portal/jobseeker/browse_jobs"));
const JobDetail = lazy(() => import("./portal/jobseeker/job_detail"));
const MyApplications = lazy(() => import("./portal/jobseeker/my_applications"));
const MyProfile = lazy(() => import("./portal/jobseeker/my_profile").then(m => ({ default: m.MyProfile })));
const Portfolio = lazy(() => import("./portal/jobseeker/portfolio").then(m => ({ default: m.Portfolio })));
const Messages = lazy(() => import("./portal/jobseeker/messages").then(m => ({ default: m.Messages })));
const SavedJobs = lazy(() => import("./portal/jobseeker/saved_jobs").then(m => ({ default: m.SavedJobs })));
const EmployerSearch = lazy(() => import("./portal/jobseeker/search"));
const EmployerDetails = lazy(() => import("./portal/jobseeker/employer_details"));
const JSMarketplaceLanding = lazy(() => import("./portal/jobseeker/marketplace"));
const JSCreateMarketPlaceListing = lazy(() => import("./portal/jobseeker/createmarketplacelisting"));
const JSMarketplaceListingDetails = lazy(() => import("./portal/jobseeker/marketlistingdetails"));

// employer
const EmployerDashboard = lazy(() => import("./portal/employer/employer_dashboard"));
const PostJob = lazy(() => import("./portal/employer/post_job"));
const MyListings = lazy(() => import("./portal/employer/my_listings"));
const ViewApplicants = lazy(() => import("./portal/employer/view_applicants").then(m => ({ default: m.ViewApplicants })));
const CandidateProfile = lazy(() => import("./portal/employer/candidate_profile").then(m => ({ default: m.CandidateProfile })));
const CandidateSearchProfile = lazy(() => import("./portal/employer/candidate_search_profile").then(m => ({ default: m.CandidateSearchProfile })));
const CandidateSearch = lazy(() => import("./portal/employer/candidate_search"));
const Shortlist = lazy(() => import("./portal/employer/shortlist").then(m => ({ default: m.Shortlist })));
const EmpMessages = lazy(() => import("./portal/employer/emp_messages").then(m => ({ default: m.EmpMessages })));
const BusinessProfile = lazy(() => import("./portal/employer/business_profile").then(m => ({ default: m.BusinessProfile })));
const Analytics = lazy(() => import("./portal/employer/analytics").then(m => ({ default: m.Analytics })));
const JobDetailsView = lazy(() => import("./portal/employer/job-details").then(m => ({ default: m.JobDetailsView })));
const MarketplaceLanding = lazy(() => import("./portal/employer/marketplace"));
const CreateMarketPlaceListing = lazy(() => import("./portal/employer/createmarketplacelisting"));
const MarketplaceListingDetails = lazy(() => import("./portal/employer/marketlistingdetails"));

// admin
const AdminMetrics = lazy(() => import("./portal/admin/admin_metrics"));

const NotFound = lazy(() => import("./NotFound"));

function AppRoutes() {
  const location = useLocation();
  const transitioning = useRouteTransition();

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return (
    <>
      <BrandLoader show={transitioning} variant="fullscreen" label="Loading" delay={150} minDuration={250} />
      <Suspense fallback={<BrandLoader show variant="fullscreen" label="Loading" delay={0} minDuration={0} />}>
        <Routes location={location}>
          {/* public */}
          <Route path="/"        element={<LandingPage />} />
          <Route path="/signin"  element={<SignIn />} />
          <Route path="/signup"  element={<SignUp />} />
          <Route path="/verify"  element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* job seeker pages */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <JobSeekerDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/jobs"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <BrowseJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <JobDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <MyApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <MyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <Portfolio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-jobs"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <SavedJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <EmployerSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer-details/:id"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <EmployerDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <JSMarketplaceLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/create"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <JSCreateMarketPlaceListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/details/:id"
            element={
              <ProtectedRoute allowedRole="jobseeker">
                <JSMarketplaceListingDetails />
              </ProtectedRoute>
            }
          />

          {/* ── employer ── */}
          <Route
            path="/employer/dashboard"
            element={
              <ProtectedRoute allowedRole="employer">
                <EmployerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/post-job"
            element={
              <ProtectedRoute allowedRole="employer">
                <PostJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/listings"
            element={
              <ProtectedRoute allowedRole="employer">
                <MyListings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/listing-details/:id"
            element={
              <ProtectedRoute allowedRole="employer">
                <JobDetailsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/applicants"
            element={
              <ProtectedRoute allowedRole="employer">
                <ViewApplicants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/candidate-search"
            element={
              <ProtectedRoute allowedRole="employer">
                <CandidateSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/applicants/:listingId"
            element={
              <ProtectedRoute allowedRole="employer">
                <ViewApplicants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/candidate/:candidateId"
            element={
              <ProtectedRoute allowedRole="employer">
                <CandidateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/candidate-search-profile/:candidateId"
            element={
              <ProtectedRoute allowedRole="employer">
                <CandidateSearchProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/shortlist"
            element={
              <ProtectedRoute allowedRole="employer">
                <Shortlist />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/messages"
            element={
              <ProtectedRoute allowedRole="employer">
                <EmpMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/profile"
            element={
              <ProtectedRoute allowedRole="employer">
                <BusinessProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/analytics"
            element={
              <ProtectedRoute allowedRole="employer">
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/marketplace"
            element={
              <ProtectedRoute allowedRole="employer">
                <MarketplaceLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/marketplace/create"
            element={
              <ProtectedRoute allowedRole="employer">
                <CreateMarketPlaceListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/marketplace/details/:id"
            element={
              <ProtectedRoute allowedRole="employer">
                <MarketplaceListingDetails />
              </ProtectedRoute>
            }
          />

          {/* ── admin ── */}
          <Route
            path="/admin/metrics"
            element={
              <AdminRoute>
                <AdminMetrics />
              </AdminRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  useEffect(() => {
    resumeAnalyticsIfConsented();
  }, []);

  return (
    <ErrorBoundary>
      <UserProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </UserProvider>
      <ConsentBanner />
    </ErrorBoundary>
  );
}
